import styled from '@emotion/styled'
import { Link } from 'found'
import { TFunction } from 'i18next'
import { useMemo, useState } from 'react'

import { defectComparator } from '../../reducers/defectsSlice'
import { color, getColor, theme } from '../../Theme'
import { Defect } from '../../types'
import { DefectAdditionalDetails, formatDescription, formatIssuedTime } from '../defects/DefectsRow'
import RestrictionIcon from '../findings/RestrictionIcon'
import RestrictionText from '../findings/RestrictionText'
import Text from '../Text'

interface Props {
  installationGroup: string
  vehicleType?: string
  defects?: Defect[]
  onCreateDefect: (serialNumber: string) => void
  t: TFunction
  nightMode: boolean
}

const ActionLink = styled(Link)`
  ${theme.text('mediumLarge', 'content', 'bold')};
  ${theme.spacing.ends('smaller')};
  ${theme.spacing.sides('normal')};
  text-decoration: none;
  line-height: 100%;
  background: ${color('primaryBlue')};
  border: none;
  color: ${color('white')};
  cursor: pointer;
  border-radius: ${theme.borderRadius.button};
`

const VehicleCard = styled.div`
  ${theme.layout.flexColumn};
  gap: ${theme.spacing.sizes.smallest};
  align-items: stretch;
  margin-top: ${theme.spacing.sizes.normal};
`

const VehicleCardHeader = styled.div`
  ${theme.layout.flexRow};
  ${theme.spacing.ends('smaller')};
  justify-content: space-between;
  align-items: center;
`

const DefectCardContainer = styled.div`
  background: ${(p) => getColor(p.theme, ['white'], ['grayDark'])};
`

const DefectHeaderContainer = styled.div`
  ${theme.layout.flexRow};
  ${theme.spacing.ends('smallest')};
  ${theme.spacing.sides('normal')};
  gap: ${theme.spacing.sizes.normal};
  align-items: center;
`

const SubHeaderText = styled(Text)`
  ${theme.text('mediumLarge', 'content', 'bold')};
  text-transform: uppercase;
  color: ${(p) => getColor(p.theme, ['black'], ['white'])};
`

const BoldText = styled.span`
  font-weight: 700;
`

const DefectCard = ({
  defect,
  t,
  nightMode,
}: {
  defect: Defect
  t: TFunction
  nightMode: boolean
}) => {
  const [open, setOpen] = useState(false)
  return (
    <DefectCardContainer>
      <DefectHeaderContainer onClick={() => setOpen(!open)}>
        <RestrictionIcon
          criticality={defect.criticality}
          usageCode={defect.restrictionCodeDescription}
          nightMode={nightMode}
          status="open"
          iconSize="default"
        />
        <Text>
          {formatIssuedTime(defect.issuedTime).dayMonth}{' '}
          <BoldText>{formatDescription(defect.defectDescription)}</BoldText>
          {defect.restrictionCodeDescription && (
            <RestrictionText>
              {t('restrictionInfos')}: {defect.restrictionCodeDescription}
            </RestrictionText>
          )}
        </Text>
      </DefectHeaderContainer>
      {open && <DefectAdditionalDetails defect={defect} t={t} nightMode={nightMode} />}
    </DefectCardContainer>
  )
}

export const VehicleDefectsList = ({
  installationGroup,
  vehicleType,
  defects = [],
  onCreateDefect,
  t,
  nightMode,
}: Props) => {
  const defectsSorted = useMemo(() => defects.slice().sort(defectComparator), [defects])

  return (
    <VehicleCard key={installationGroup}>
      <VehicleCardHeader key="header">
        <SubHeaderText>
          {installationGroup} {vehicleType ?? ''}
        </SubHeaderText>
        <ActionLink
          to={{
            pathname: '/create-defect',
            state: { from: window.location.pathname },
          }}
          onClick={() => onCreateDefect(installationGroup)}
        >
          {t('defect.createNewShort')}
        </ActionLink>
      </VehicleCardHeader>

      {defectsSorted.map((defect) => (
        <DefectCard key={defect.id} defect={defect} t={t} nightMode={nightMode} />
      ))}
    </VehicleCard>
  )
}
